import { useEffect, useState } from 'react'

// @mui
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Icon from '@mui/material/Icon'

// MD
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'

// Layout
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Loader from 'examples/Loader'

// Services
import { useAppServices } from 'hook/services'
import { setNotification, useNotifcation } from 'context/notification'

function GeoAllowlist() {
  const AppService = useAppServices()
  const [, dispatchNotif] = useNotifcation()

  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [ip, setIp] = useState('')
  const [label, setLabel] = useState('')
  const [adding, setAdding] = useState(false)

  const notify = (msg, type = 'success') =>
    setNotification(dispatchNotif, { open: true, message: msg, severity: type })

  const fetchAll = async () => {
    setLoading(true)
    try {
      const { response } = await AppService.geoAllowlist.list({})
      setRows(response?.success ? response.data || [] : [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAdd = async () => {
    const value = ip.trim()
    if (!value) return
    setAdding(true)
    try {
      // The OTP modal (same one used for bookings/settings actions) pops up
      // automatically here — this path is in OTP_SENSITIVE_POST_PATTERNS.
      const { response } = await AppService.geoAllowlist.add({
        payload: { ip: value, label: label.trim() },
      })
      if (response?.success) {
        setIp('')
        setLabel('')
        notify(response.message || 'IP added.')
        fetchAll()
      } else {
        notify(response?.message || 'Could not add IP.', 'error')
      }
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async (row) => {
    if (!window.confirm(`Remove ${row.ip} from the bypass list? It will no longer skip the AU/GB region check.`))
      return
    const { response } = await AppService.geoAllowlist.remove({ params: { id: row._id } })
    if (response?.success) {
      notify('IP removed.')
      fetchAll()
    } else {
      notify(response?.message || 'Could not remove IP.', 'error')
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading ? (
        <Loader />
      ) : (
        <MDBox pt={6} pb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    Region-Block Bypass IPs
                  </MDTypography>
                  <MDTypography variant="caption" color="white">
                    These IPs skip the Australia/UK-only check site-wide. Changes go live immediately.
                  </MDTypography>
                </MDBox>

                <MDBox p={3}>
                  <MDTypography variant="caption" color="text" mb={2} display="block">
                    Anyone on this list can reach the site from any country — only add an address you
                    trust. Adding or removing one requires a fresh email code, same as other sensitive
                    actions.
                  </MDTypography>

                  <MDBox display="flex" gap={1} mb={3} flexWrap="wrap">
                    <TextField
                      size="small"
                      label="IP address or CIDR"
                      placeholder="e.g. 103.21.45.10"
                      value={ip}
                      onChange={(e) => setIp(e.target.value)}
                      sx={{ minWidth: 220 }}
                    />
                    <TextField
                      size="small"
                      label="Label (optional)"
                      placeholder="e.g. Cy — travelling, Oct 2026"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      sx={{ minWidth: 260, flex: 1 }}
                    />
                    <MDButton color="info" onClick={handleAdd} disabled={!ip.trim() || adding}>
                      {adding ? 'Adding…' : 'Add'}
                    </MDButton>
                  </MDBox>

                  {rows.length === 0 ? (
                    <MDTypography variant="body2" color="text">
                      No bypass IPs — only Australia and the UK can reach the site right now.
                    </MDTypography>
                  ) : (
                    rows.map((row) => (
                      <MDBox
                        key={row._id}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        py={1.25}
                        borderBottom="1px solid #eee"
                      >
                        <MDBox flex={1}>
                          <MDTypography variant="body2" fontWeight="medium">
                            {row.ip}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {row.label ? `${row.label} — ` : ''}
                            added by {row.addedBy?.name || row.addedBy?.email || 'unknown'} on{' '}
                            {new Date(row.createdAt).toLocaleDateString()}
                          </MDTypography>
                        </MDBox>
                        <Tooltip title="Remove">
                          <IconButton size="small" onClick={() => handleRemove(row)}>
                            <Icon fontSize="small">delete</Icon>
                          </IconButton>
                        </Tooltip>
                      </MDBox>
                    ))
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      )}
    </DashboardLayout>
  )
}

export default GeoAllowlist
